import { useMutation, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../api.ts";

/**
 * Sign in and sign out.
 *
 * Plain `fetch` rather than the Eden client: Better Auth mounts its own handler at
 * `/api/auth/*` and is not part of the API's typed route graph, so there is nothing for
 * Eden to derive. `credentials: "include"` is the whole authentication — the session is an
 * HttpOnly cookie (P3), so there is no token to store and nothing to attach by hand.
 */

const AUTH_BASE = `${API_URL}/api/auth`;

export class SignInError extends Error {}

export function useSignIn() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      const response = await fetch(`${AUTH_BASE}/sign-in/email`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input),
      });

      // Deliberately one message for every failure. A wrong password and an unknown address
      // must not be distinguishable, which is the same rule `http/errors.ts` follows.
      if (!response.ok) throw new SignInError(String(response.status));
      return response.json() as Promise<unknown>;
    },
    onSuccess: () => {
      // Everything cached was fetched as nobody. Clearing beats invalidating: a stale
      // franchise list belonging to the previous session is worse than a second of loading.
      client.clear();
    },
  });
}

export function useSignOut() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => fetch(`${AUTH_BASE}/sign-out`, { method: "POST", credentials: "include" }),
    onSettled: () => {
      client.clear();
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: { currentPassword: string; newPassword: string }) => {
      const response = await fetch(`${AUTH_BASE}/change-password`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...input, revokeOtherSessions: true }),
      });
      if (!response.ok) throw new Error(String(response.status));
    },
  });
}
